import { AsyncSelect, type IAsyncSelectProps } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { approverRepository } from '@webapp/platform/approval-routing/data-access/repositories/ApproverRepository'
import { RequestableApprover } from '@webapp/platform/approval-routing/entities/RequestableApprover'
import messages from './messages'

export interface IApproverSelectProps
  extends IAsyncSelectProps<RequestableApprover> {
  accountCodeIds?: number[]
  amount: number
  baseContentTypeId?: number
  secondaryCategoryId?: number | null
  userId?: number
}

export const ApproverSelect = ({
  accountCodeIds = [],
  amount,
  baseContentTypeId,
  secondaryCategoryId,
  userId,
  ...otherProps
}: IApproverSelectProps) => {
  const intl = useProcurifyIntl()

  const loadOptions: IApproverSelectProps['loadOptions'] = (
    inputValue,
    pagination,
    callback
  ) => {
    if (!baseContentTypeId || secondaryCategoryId === undefined) {
      callback({
        options: [],
        pagination: {},
      })
      return
    }

    const params = {
      ...(inputValue && { search: inputValue }),
      pageSize: 20,
    }

    approverRepository
      .getRequestableApprovers(
        {
          accountCodeIds,
          amount,
          baseContentTypeId,
          secondaryCategoryId,
        },
        params
      )
      .then((res) => {
        callback({
          options: res.data.map(
            (approver) => new RequestableApprover(approver)
          ),
          pagination: {
            hasMore: res.pagination
              ? res.pagination?.page < res.pagination?.numPages
              : false,
            page: pagination.page + 1,
          },
        })
      })
      .catch(() => null)
  }

  return (
    <AsyncSelect<RequestableApprover>
      cacheOptions={true}
      getOptionLabel={(approver) =>
        `${approver.fullName}${
          approver.userId === userId
            ? ` (${intl.formatMessage(messages.selfApprovalLabel)})`
            : ''
        }`
      }
      getOptionValue={(approver) => approver.userId.toString()}
      loadOptions={loadOptions}
      placeholder={intl.formatMessage(messages.placeholderText)}
      {...otherProps}
    />
  )
}

ApproverSelect.displayName = 'ApproverSelect'
